import React from 'react'
import styled from 'styled-components'
import colours from '../lib/colours'
import ReadMoreButton from './read-more-button'
import Callout from './callout'

const TableContainer = styled.div`
  display: inline-block;
  margin: 10px 0;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
`

const TableHeader = styled.th`
  background: ${colours.darkPurple};
  border: 0.125rem solid ${colours.white};
  border-left: none;
  border-right: none;
  color: ${colours.white};
  font-size: 13px;
  font-weight: 300;
  line-height: 1.5;
  padding: 13px 15px;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: -0.2px;
`

const TableRow = styled.tr`
  color: ${colours.darkPurple};
  font-size: 13px;
  font-weight: 300;
  line-height: 1.5;
  letter-spacing: -0.2px;
`
const StandardTableRow = styled(TableRow)`
  background: ${colours.lightGray};
`

const HoverTableRow = styled(StandardTableRow)`
  :hover {
    background: ${colours.lightPurple};
  }
`

const WarningTableRow = styled(TableRow)`
  background: ${colours.lightGreenBackground};
  padding: 0;
`

const TableCell = styled.td`
  border-top: 0.125rem solid ${colours.white};
  border-left: none;
  border-right: none;
  padding: 13px 15px;
`

const buttonCellStyle = {
  width: '10px',
  padding: '2px 3px'
}

export interface ITableRowData {
  rowData: any[]
  warningData: any[]
}

interface IProps {
  headers: string[]
  rows: ITableRowData[]
  fixed?: boolean
  onRowMouseEnter?: any
  onRowMouseLeave?: any
  getRowKey?: any
}

const Table = class extends React.Component<IProps, {}> {
  static defaultProps: { onRowMouseEnter: undefined; onRowMouseLeave: undefined; getRowKey: (row: any[]) => any }

  render() {
    const { headers, rows, fixed, onRowMouseEnter, onRowMouseLeave, getRowKey } = this.props

    const renderWarningRows = (row: ITableRowData) => {
      return row.warningData.length ? (
        <WarningTableRow>
          <td colSpan={row.rowData.length}>
            {row.warningData.map((z: any[]) => {
              return (
                <Callout key={`${row.rowData[0]}${z[0]}`} type="warning" inTable={true}>
                  {z[0]}
                  <ReadMoreButton label="Read more" onClick={z[1]} fontSize="13px" />
                </Callout>
              )
            })}
          </td>
        </WarningTableRow>
      ) : null
    }

    const renderCells = (row: ITableRowData) => {
      return row.rowData.map((y: string, index: number) => (
        <TableCell key={index} style={typeof y === 'string' ? {} : buttonCellStyle}>
          {y}
        </TableCell>
      ))
    }

    const renderStandardRow = (row: ITableRowData) => {
      return (
        <React.Fragment key={getRowKey(row.rowData)}>
          <StandardTableRow>{renderCells(row)}</StandardTableRow>
          {renderWarningRows(row)}
        </React.Fragment>
      )
    }

    const renderHoverRow = (row: ITableRowData) => {
      return (
        <React.Fragment key={getRowKey(row.rowData)}>
          <HoverTableRow
            onMouseEnter={() => onRowMouseEnter(getRowKey(row.rowData))}
            onMouseLeave={() => onRowMouseLeave(getRowKey(row.rowData))}
          >
            {renderCells(row)}
          </HoverTableRow>
          {renderWarningRows(row)}
        </React.Fragment>
      )
    }

    return (
      <TableContainer>
        <table style={{ width: '100%', tableLayout: fixed ? 'fixed' : 'auto' }}>
          <thead>
            <tr>
              {headers.map((x: string) => (
                <TableHeader key={x}>
                  <div>{x}</div>
                </TableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((x: any) => {
              return !!onRowMouseEnter ? renderHoverRow(x) : renderStandardRow(x)
            })}
          </tbody>
        </table>
      </TableContainer>
    )
  }
}

Table.defaultProps = {
  onRowMouseEnter: undefined,
  onRowMouseLeave: undefined,
  getRowKey: (row: any[]) => row[0]
}

export default Table
